import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Loader2, X } from 'lucide-react'
import { lcTestCompletionAdminApi } from '@/api/lcTestCompletion'

interface Props {
  open: boolean
  organizationId: number
  centerName: string
  onClose: () => void
  onReset?: () => void
}

export function canExecuteCenterReset(centerName: string, confirmation: string, reason: string): boolean {
  return confirmation.trim() === centerName && reason.trim().length > 0
}

export function CenterLearningDataResetDialog({ open, organizationId, centerName, onClose, onReset }: Props) {
  const queryClient = useQueryClient()
  const [confirmation, setConfirmation] = useState('')
  const [reason, setReason] = useState('')
  const preview = useQuery({
    queryKey: ['admin', 'organizations', organizationId, 'learning-data-reset-preview'],
    queryFn: () => lcTestCompletionAdminApi.previewReset(organizationId),
    enabled: open,
  })
  useEffect(() => {
    if (open) {
      setConfirmation('')
      setReason('')
    }
  }, [open])
  const reset = useMutation({
    mutationFn: () => lcTestCompletionAdminApi.resetCenter(organizationId, reason.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations', organizationId, 'test-completion-runs'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations', organizationId, 'learning-data-reset-preview'] })
      onReset?.()
      onClose()
    },
  })

  if (!open) return null
  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/55 p-4" role="dialog" aria-modal="true" aria-labelledby="center-reset-title">
      <div className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-red-600">Irreversible action</p>
            <h2 id="center-reset-title" className="mt-1 text-lg font-bold text-slate-950">센터 학습 데이터 초기화</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="닫기" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </header>
        <div className="max-h-[70vh] space-y-5 overflow-y-auto px-6 py-5">
          <div className="flex gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <p>원생 계정·등록·결제·수강권은 유지하고, 이 센터의 학습 진도·답안·채점·출결·게임 점수를 삭제합니다. 실행 감사 이력은 보존됩니다.</p>
          </div>
          {preview.isLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />삭제 범위를 계산하는 중…</div>
          ) : preview.data ? (
            <div>
              <div className="mb-3 flex items-end justify-between">
                <div><span className="text-3xl font-bold text-slate-950">{preview.data.total.toLocaleString()}</span><span className="ml-1 text-sm text-slate-500">건</span></div>
                <span className="text-sm text-slate-500">원생 {preview.data.learnerCount.toLocaleString()}명</span>
              </div>
              <div className="grid grid-cols-2 gap-2 rounded-xl bg-slate-50 p-3 sm:grid-cols-3">
                {Object.entries(preview.data.counts).map(([key, count]) => (
                  <div key={key} className="rounded-lg bg-white px-3 py-2 ring-1 ring-slate-200">
                    <div className="truncate text-xs text-slate-500" title={key}>{key}</div>
                    <div className="font-semibold text-slate-900">{count.toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : <p className="text-sm text-red-600">삭제 범위를 불러오지 못했습니다.</p>}
          <label className="block text-sm font-medium text-slate-700">
            확인을 위해 센터명 <strong>{centerName}</strong>을 입력하세요
            <input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2" placeholder={centerName} />
          </label>
          <label className="block text-sm font-medium text-slate-700">
            초기화 사유
            <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2" placeholder="검증 완료 및 상용 전환 준비" />
          </label>
          {reset.isError && <p className="text-sm text-red-600">초기화에 실패했습니다. 활성 실행과 센터 운영 모드를 확인해 주세요.</p>}
        </div>
        <footer className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200">취소</button>
          <button
            type="button"
            onClick={() => reset.mutate()}
            disabled={reset.isPending || preview.isLoading || !canExecuteCenterReset(centerName, confirmation, reason)}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {reset.isPending ? '초기화 중…' : `${preview.data?.total.toLocaleString() ?? 0}건 초기화`}
          </button>
        </footer>
      </div>
    </div>
  )
}
